import { useCallback, useEffect, useRef } from "react";
import { useSearch } from "wouter/use-browser-location";
import { useListState } from "./use-list-state";

/** A search link must also work when navigating to the page already open. */
export function useSearchQueryParam() {
  const search = useSearch();
  const query = new URLSearchParams(search).get("q");
  const [value, setValue] = useListState("list-search", () => query ?? "", query ?? "");
  const previousQuery = useRef(query);
  useEffect(() => {
    // Restoring a list must not overwrite its remembered search on mount.
    if (previousQuery.current === query) return;
    previousQuery.current = query;
    setValue(query ?? "");
  }, [query, setValue]);
  return [value, setValue, query] as const;
}

/** Search links broaden a saved filter without changing the user's preferences. */
export function useSearchFilterOverride<T>(
  query: string | null,
  saved: [T, (next: T) => void],
  searchDefault: T,
  key = "search-filter-override",
) {
  const [savedValue, saveValue] = saved;
  const [override, setOverride] = useListState<T | undefined>(
    key, () => query !== null ? searchDefault : undefined, query ?? "",
  );
  const setValue = useCallback((next: T) => {
    setOverride(next);
    saveValue(next);
  }, [saveValue]);
  return [override ?? savedValue, setValue] as const;
}